import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async"; // ✅ ADDED
import {
  ChevronLeft, ChevronRight, TrendingUp, Calendar, ArrowRight, MapPin,
  CheckCircle, Phone, MessageCircle, Mail, Star, Info, X, Search,
  ShieldCheck, Zap, Heart, Users, Facebook, Twitter, Instagram, Linkedin
} from "lucide-react";
import "../../../src/home.css";

// Assets
import banner3 from "./Assets/eventbanner4.jpg";
import banner4 from "./Assets/eventsidebanner.jpeg"

const Home = () => {

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);

  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get("https://ezer-eventapp.onrender.com/events")
      .then((res) => {
        const formatted = res.data.map((event) => ({
          ...event,
          displayPrice: Math.min(...(event.packages?.flatMap(g => g.packages?.map(p => p.price)) || [0])),
          image: event.images?.[0] || "",
          location: event.locations?.[0]?.city || "Unknown",
        }));
        setData(formatted);
        setFilteredData(formatted);
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    let result = data;
    if (selectedCategory !== "All") {
      result = result.filter(item => item.categories?.includes(selectedCategory));
    }
    if (searchQuery) {
      result = result.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredData(result);
  }, [selectedCategory, searchQuery, data]);

  useEffect(() => {
    const cards = document.querySelectorAll(".event-card");
    cards.forEach((card) => {
      card.addEventListener("mousemove", (e) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty("--x", `${e.clientX - rect.left}px`);
        card.style.setProperty("--y", `${e.clientY - rect.top}px`);
      });
    });
  }, []);

  return (
    <div className="home-wrapper">

      {/* ✅ SEO ADDED */}
      <Helmet>
        <title>Ezer Booking | Book Event Services Without Login</title>

        <meta
          name="description"
          content="Book photographers, decorators, caterers, DJs, and venues without login. Fast and easy event booking platform in India."
        />

        <meta
          name="keywords"
          content="event booking without login, photographers booking india, wedding services hyderabad, decorators booking"
        />

        <link rel="canonical" href="https://ezerbooking.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="Ezer Booking - Event Booking Platform" />
        <meta property="og:description" content="Book event services instantly without login." />
        <meta property="og:url" content="https://ezerbooking.com/" />
      </Helmet>



      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="hero-overlay"></div>

        <div className="container hero-content">
          <div className="row align-items-center">

            {/* LEFT CONTENT */}
            <div className="col-lg-6 text-center text-lg-start">

              <span className="hero-badge">
                ✨ Smart Event Booking Platform
              </span>

              <h1 className="hero-title display-4 fw-bold lh-sm">
                Book Amazing Events <br />
                <span className="fw-bolder text-primary">Without Logging In</span><br />
                <span className="fw-light text-muted">Anytime, Anywhere</span>
              </h1>

              <p className="hero-desc">
                Book photographers, decorators, venues, and event services instantly
                without login. Perfect for weddings, parties, and corporate events across India.
              </p>

              <div className="hero-buttons">
                <button
                  onClick={() => navigate("/FlashDeals")}
                  className="btn btn-primary-lg fw-bold"
                >
                  Explore Events →
                </button>

                <button
                  onClick={() => navigate("/contact")}
                  className="light p-3 fw-bold"
                >
                  Contact Us
                </button>
              </div>

            </div>

            {/* RIGHT SIDE IMAGE */}
            <div className="col-lg-6 text-center mt-5 mt-lg-0">
              <div className="hero-image-wrapper">
                <img
                  src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819"
                  alt="event"
                  className="hero-image"
                />
              </div>
            </div>

          </div>
        </div>
      </section>
      {/* Our Event Categories */}
      <section className="event-types-section">
        <div className="container">

          {/* Animated Title */}
          <h2 className="event-title">
            {"Our Event Categories".split("").map((char, i) => (
              <span key={i} style={{ animationDelay: `${i * 0.05}s` }}>
                {char === " " ? "\u00A0" : char}
              </span>
            ))}
          </h2>
          <div className="row g-5">
            {/* Social Events */}
            <div className="col-lg-4">
              <div className="event-card social">
                <h3>Social Events</h3>
                <h6 className="event-subtitle">Fun & Entertainment</h6>
                <ul>
                  <li>🎉 Parties</li>
                  <li>🎊 Festivals</li>
                  <li>✨ Celebrations</li>
                </ul>
                <button onClick={() => navigate("/FlashDeals")} className="event-btn">Explore</button>
              </div>
            </div>
            {/* Corporate Events */}
            <div className="col-lg-4 col-md-6 col-12">
              <div className="event-card corporate">
                <h3>Corporate Events</h3>
                <h6 className="event-subtitle">Business & Professional</h6>

                <ul>
                  <li>📊 Meetings</li>
                  <li>🎤 Conferences</li>
                  <li>🏢 Corporate Gatherings</li>
                </ul>

                <button onClick={() => navigate("/FlashDeals")} className="event-btn">Explore</button>
              </div>
            </div>

            {/* Personal Events */}
            <div className="col-lg-4">
              <div className="event-card personal">
                <h3>Personal Events</h3>
                <h6 className="event-subtitle">Private Celebrations</h6>

                <ul>
                  <li>💍 Weddings</li>
                  <li>🎂 Birthdays</li>
                  <li>🎈 Private Parties</li>
                </ul>

                <button onClick={() => navigate("/FlashDeals")} className="event-btn">Explore</button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}

      <section className="features-section">
        <div className="container">
          {/* Letter Animation Title */}
          <h1 className="features-title">
            {"Why Choose Us".split("").map((char, i) => (
              <span key={i} style={{ animationDelay: `${i * 0.05}s` }}>
                {char === " " ? "\u00A0" : char}
              </span>
            ))}
          </h1>

          <div className="row g-5">

            <div className="col-lg-4">
              <div className="feature-card-custom">
                <div className="feature-icon primary">
                  <ShieldCheck size={32} />
                </div>
                <h4>Secure Booking</h4>
                <p>Encrypted transactions and verified organizers ensure your peace of mind.</p>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="feature-card-custom">
                <div className="feature-icon warning">
                  <Zap size={32} />
                </div>
                <h4>Instant Confirmation</h4>
                <p>Get your tickets delivered instantly via Email and WhatsApp.</p>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="feature-card-custom">
                <div className="feature-icon danger">
                  <Heart size={32} />
                </div>
                <h4>Top-Rated Support</h4>
                <p>Our dedicated concierge team is here to help 24/7.</p>
              </div>
            </div>

          </div>
        </div>
      </section>
      {/* TRENDING EVENTS */}
      <section className="bg-(135deg, #020617, #0F172A) py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <h3 className="fw-bold m-0"><TrendingUp className="text-danger me-2" /> Trending Events</h3>
              <p className="text-white mb-0">The most anticipated events happening now</p>
            </div>
            <div className="d-flex gap-2">
              <button onClick={() => scrollRef.current.scrollBy({ left: -340, behavior: "smooth" })} className="btn btn-white shadow-sm rounded-circle"><ChevronLeft /></button>
              <button onClick={() => scrollRef.current.scrollBy({ left: 340, behavior: "smooth" })} className="btn btn-white shadow-sm rounded-circle"><ChevronRight /></button>
            </div>
          </div>
          <div
            ref={scrollRef}
            className="d-flex gap-3 overflow-auto pb-4 no-scrollbar px-2"
          >
            {filteredData.map((item) => (
              <div key={item._id} style={{ minWidth: "320px" }}>
                <ProductCard item={item} onDetails={() => setSelectedItem(item)} />
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* MODALS */}
      {selectedItem && (
        <DetailsModal item={selectedItem} onClose={() => setSelectedItem(null)} onBookPackage={(pkg, eventName) => {
          setBookingDetails({ ...pkg, eventName, vendorName: selectedItem.name, vendorId: selectedItem._id, locations: selectedItem.locations });
          setSelectedItem(null);
        }} />
      )}
      {bookingDetails && <BookingModal bookingDetails={bookingDetails} onClose={() => setBookingDetails(null)} />}
    </div>
  );
};

/* --- PRODUCT CARD --- */
const ProductCard = ({ item, onDetails }) => (
  <div className="home-card card border-0 shadow-sm h-100 rounded-4 overflow-hidden">
    <div className="home-img-wrapper" style={{ height: 220, cursor: "pointer" }} onClick={onDetails}>
      <img src={item.image || "/no-image.png"} className="w-100 h-100 object-fit-cover" alt={item.name} />
      <div className="price-tag">₹{item.displayPrice}</div>
    </div>
    <div className="card-body p-4">
      <h5 className="fw-bold text-truncate">{item.name}</h5>
      <p className="text-muted small mb-3 d-flex align-items-center gap-1">
        <MapPin size={14} className="text-primary" /> {item.location}
      </p>
      <div className="d-flex justify-content-between align-items-center">
        <div className="small text-success fw-bold d-flex align-items-center gap-1">
          <CheckCircle size={14} /> Available Now
        </div>
        <button onClick={onDetails} className="btn btn-dark btn-sm rounded-pill px-4">View</button>
      </div>
    </div>
  </div>
);

/* --- HERO SLIDER --- */
const HeroSlider = () => {
  const slides = [{ img: banner3 }, { img: banner4 }];
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrent((p) => (p === slides.length - 1 ? 0 : p + 1)), 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="slider-frame rounded-5 shadow-2xl overflow-hidden position-relative" style={{ height: 450 }}>
      {slides.map((slide, i) => (
        <img key={i} src={slide.img} className={`position-absolute w-100 h-100 object-fit-cover transition-all duration-700 ${current === i ? 'opacity-100' : 'opacity-0'}`} alt="" />
      ))}
    </div>
  );
};

/* --- DETAILS MODAL & BOOKING MODAL (Condensed for brevity, same logic as before) --- */
const DetailsModal = ({ item, onClose, onBookPackage }) => {
  return (
    <div className="modal d-block show" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0 rounded-4 overflow-hidden">
          <button onClick={onClose} className="btn-close-custom position-absolute end-0 top-0 m-3 z-3"><X /></button>
          <div className="modal-body p-0">
            <div className="row g-0">
              <div className="col-lg-8">
                <img src={item.image} className="w-100" style={{ height: 400, objectFit: "cover" }} alt="" />
                <div className="p-4">
                  <h2 className="fw-bold">{item.name}</h2>
                  <p className="text-muted">{item.description}</p>
                  <div className="row g-3">
                    {item.packages?.map((group) => group.packages.map((pkg, j) => (
                      <div key={j} className="col-md-6">
                        <div className="p-3 border rounded-4 hover-shadow transition">
                          <h6 className="fw-bold">{pkg.packageName}</h6>
                          <h4 className="fw-bold text-primary">₹{pkg.price}</h4>
                          <button onClick={() => onBookPackage(pkg, group.eventName)} className="btn btn-dark w-100 rounded-pill mt-2">Book Now</button>
                        </div>
                      </div>
                    )))}
                  </div>
                </div>
              </div>
              <div className="col-lg-4 bg-light p-4">
                <h5 className="fw-bold mb-4">Venue & Contact</h5>
                {item.locations?.map((loc, i) => (
                  <div key={i} className="bg-white p-3 rounded-3 mb-3 border">
                    <div className="fw-bold small">{loc.venue}</div>
                    <div className="text-muted tiny">{loc.address}</div>
                  </div>
                ))}
                <div className="d-grid gap-2 mt-4">
                  <div className="d-flex align-items-center gap-2 small text-muted"><Phone size={14} /> {item.contact?.phone}</div>
                  <div className="d-flex align-items-center gap-2 small text-muted"><Mail size={14} /> {item.contact?.email}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingModal = ({ bookingDetails, onClose }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Booking Successful!");
    onClose();
  };

  return (
    <div className="modal d-block show" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1100 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 rounded-5 p-4">
          <h4 className="fw-bold mb-4">Complete Your Booking</h4>
          <div className="bg-primary-soft p-3 rounded-4 mb-4">
            <div className="small text-primary fw-bold">{bookingDetails.eventName}</div>
            <div className="fw-bold">{bookingDetails.packageName} - ₹{bookingDetails.price}</div>
          </div>
          <form onSubmit={handleSubmit}>
            <input className="form-control rounded-pill mb-3 py-2 px-3" placeholder="Full Name" required />
            <input className="form-control rounded-pill mb-3 py-2 px-3" placeholder="Phone Number" required />
            <button className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow">Confirm & Pay</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Home;